import React, { useMemo } from "react";
import { useNavigate } from "react-router";
import { Box, Button } from "@mui/material";
import * as Sentry from "@sentry/react";
import JSZip from "jszip";
import { useSnackbar } from "notistack";

import analytics from "fond/analytics";
import { useCreateServicesReportMutation, useDeleteReportMutation, useImportReportMutation } from "fond/api";
import { useQueryParams } from "fond/utils/hooks";

import type { ReportImportFormData } from "../types";

import ImportSettingsForm from "./ImportSettingsForm";

const ImportReportForm: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const folderId = useQueryParams("folderId");
  const navigate = useNavigate();
  const [createReport] = useCreateServicesReportMutation();
  const [importReport] = useImportReportMutation();
  const [deleteReport] = useDeleteReportMutation();

  const initialValues: Partial<ReportImportFormData> = useMemo(
    () => ({
      Name: "",
      Description: "",
      Project: null,
      Version: null,
      CTOFiles: [],
      CFOFiles: [],
    }),
    []
  );

  const handleOnCancel = () => {
    navigate(-1);
  };

  /**
   * On submit function called when the form is submitted and valid
   */
  const handleOnSubmit = async (values: ReportImportFormData) => {
    let createdReportId: string | undefined;
    try {
      const { CFOFiles, CTSFiles, Name, Description, Version } = values;

      // Create the base report entity so that we can then import the require files into
      const report = await createReport({
        Name,
        Description: Description ?? "",
        FolderID: folderId,
        ...(Version?.ID ? { VersionID: Version.ID } : {}),
      }).unwrap();
      createdReportId = report.ID;

      // Create a zip file for CTS and CFO files & post it as FormData
      const zip = new JSZip();
      [...CFOFiles, ...CTSFiles].forEach((file) => {
        zip.file(file.name, file);
      });
      const data = await zip.generateAsync({ type: "blob" });
      const formData = new FormData();
      formData.append("ReportContent", data);

      // Import the reports data
      await importReport({
        reportId: report.ID,
        formData,
      }).unwrap();

      analytics.track("Report.Import.Report Imported", { reportId: report.ID }); // Track initial generation of a report

      navigate(`/reports/${report.ID}/poll`);
    } catch (e) {
      Sentry.captureException(e);
      if (createdReportId) {
        deleteReport({ ID: createdReportId });
      }
      enqueueSnackbar("Failed to import report", {
        action: (
          <Button onClick={() => handleOnSubmit(values)} color="inherit">
            Retry
          </Button>
        ),
      });
    }
  };

  return (
    <Box pt={4}>
      <ImportSettingsForm initialValues={initialValues} onSubmit={handleOnSubmit} onCancel={handleOnCancel} />
    </Box>
  );
};

export default ImportReportForm;
